/**
 * WorkerProfile — worker self-service profile page.
 *
 * Tab layout (post privacy audit 2026-05-22):
 *   1. Personal — phone, avatar, date_of_birth (with opt-out hint)
 *   2. Employment — worker_code, position, department,
 *      employment_type, hire_date, status (read-only HR-managed)
 *   3. Emergency — opt-in collapsed card; only shown after worker
 *      explicitly chooses to provide a contact, with explicit
 *      purpose disclosure ("Optional. Only HR. Used in workplace
 *      emergency.")
 *   4. Documents — list of WorkerDocument rows for this worker
 *   5. Notifications — channel toggles
 *   6. Devices — push device list with Revoke
 *   7. Privacy — "What we collect" disclosure + GDPR data export +
 *      account deletion
 *
 * Address tab + home_address_* columns dropped in alembic 033 +
 * api/me.ts MeHomeAddress removal. See svdy/PRIVACY_AUDIT_2026-05-22.md
 * for the GDPR Art. 5(1)(c) data-minimization rationale.
 *
 * Each editable tab has its own dialog so a Save commits only that
 * tab's payload — partial PATCH semantics on /v1/me/profile back
 * this; sending only `emergency_contact` doesn't reset other fields.
 */

import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert, Avatar, Box, Button, Chip, CircularProgress, Dialog,
  DialogActions, DialogContent, DialogContentText, DialogTitle, Divider, MenuItem, Paper,
  Snackbar, Stack, Switch, Tab, Tabs, TextField, Tooltip, Typography,
} from '@mui/material';
import {
  EditOutlined, EmailOutlined, PhoneOutlined,
  InsertDriveFileOutlined, OpenInNewOutlined, BlockOutlined,
  ArrowForwardOutlined, FaceOutlined,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { ApiError, meApi } from '../../api';
import { useTranslation } from 'react-i18next';
import { useDocumentViewer } from '../../components/common/useDocumentViewer';
import { downloadText } from '../../utils/downloadFile';
import { translateApiError } from '../../i18n/translateApiError';
import type {
  MeEmergencyContact, MeProfile, MeProfileUpdate,
  MeWorkerDocument, MeDeviceResponse,
} from '../../api/me';
import {
  digestsApi,
  type DigestSubscriptionResponse,
  type DigestCadence,
} from '../../api/notifications';
import PhoneField from '../../components/forms/PhoneField';
import { useRoleProfile } from '../../hooks/useRoleProfile';

import DateField from '../../components/inputs/DateField';
import { activeLocale } from '../../utils/datetime';
type TabKey = 'personal' | 'employment' | 'emergency' | 'documents' | 'notifications' | 'devices' | 'privacy';

// Canonical emergency-contact relationship codes — MUST match svdy-mobile's
// personal-edit RELATIONSHIP_OPTIONS so a contact stored on one client reads
// back correctly on the other (mobile stores the code; web maps code → label
// via worker:profile.rel.*). Legacy free-text values fall through to display
// as-is.
const RELATIONSHIP_CODES = ['spouse', 'partner', 'parent', 'child', 'sibling', 'friend', 'other'] as const;

const Row: React.FC<{ label: string; value?: string | null }> = ({ label, value }) => (
  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 0.5 }}>
    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
      {label}
    </Typography>
    <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.8125rem', textAlign: 'right' }}>
      {value || '—'}
    </Typography>
  </Box>
);

function fmtBytes(b: number | null): string {
  if (b == null) return '—';
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  return `${(b / 1024 / 1024).toFixed(1)} MB`;
}

// Mirror of admin Notifications.tsx tier comparison so worker portal
// gates the digest UI consistently. Pro+ unlocks email digests.
const TIER_RANK: Record<string, number> = {
  free: 0, starter: 1, professional: 2, enterprise: 3,
};
const tierAtLeast = (actual: string | null | undefined, required: string): boolean =>
  (TIER_RANK[actual ?? ''] ?? -1) >= (TIER_RANK[required] ?? 99);

const WorkerProfile: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  // Licence for the Privacy tab's face card. Same module-level store the sidebar
  // and the route boundary read, so the three cannot disagree about whether this
  // org does face matching.
  const { role } = useRoleProfile();
  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState<MeProfile | null>(null);
  const [docs, setDocs] = useState<MeWorkerDocument[]>([]);
  const [docsLoading, setDocsLoading] = useState(false);
  const { openDocument, viewer } = useDocumentViewer();
  // Notification preferences (NotificationPreference.channels JSONB).
  // Lazy-loaded on first tab visit so the Profile page's other
  // tabs don't pay a second round-trip for an unrelated read.
  // Defaults match the backend model column so a not-yet-loaded
  // state still renders sane Switch positions.
  const [prefs, setPrefs] = useState<Record<string, boolean>>({
    in_app: true, email: true, push: true, whatsapp: false, clock_reminder: true,
  });
  const [prefsLoaded, setPrefsLoaded] = useState(false);
  const [prefsBusy, setPrefsBusy] = useState(false);
  const [prefsError, setPrefsError] = useState<string | null>(null);
  // P0.3 — worker's own digest subscriptions (only the worker-self
  // report types are exposed here; admin-targeted reports stay in
  // System / Settings / Notifications). Lazy-loaded on Notifications
  // tab visit, mirroring the prefs lifecycle.
  const [digests, setDigests] = useState<DigestSubscriptionResponse[]>([]);
  const [digestsLoaded, setDigestsLoaded] = useState(false);
  const [digestsBusy, setDigestsBusy] = useState(false);
  // Worker's own devices (HANDOFF Addendum 18). Lazy-loaded on
  // first visit to the Devices tab. include_revoked=false on the
  // worker side — the audit trail belongs to the admin Devices
  // page; the worker sees only the live devices they can act on.
  const [devices, setDevices] = useState<MeDeviceResponse[]>([]);
  const [devicesLoaded, setDevicesLoaded] = useState(false);
  const [devicesLoading, setDevicesLoading] = useState(false);
  const [devicesError, setDevicesError] = useState<string | null>(null);
  const [revokeDevTarget, setRevokeDevTarget] = useState<MeDeviceResponse | null>(null);
  const [revokingDev, setRevokingDev] = useState(false);
  const [tab, setTab] = useState<TabKey>('personal');
  const [toast, setToast] = useState<string | null>(null);

  // GDPR (Privacy tab) — self-service data export + deletion request.
  const [exportLoading, setExportLoading] = useState(false);
  const [deletionLoading, setDeletionLoading] = useState(false);
  const [deletionConfirmOpen, setDeletionConfirmOpen] = useState(false);

  // Edit dialogs — one per tab, simpler than a single mega-form.
  const [personalOpen, setPersonalOpen] = useState(false);
  const [emergencyOpen, setEmergencyOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  // Per-dialog form state.
  const [personalForm, setPersonalForm] = useState({
    phone: '', avatar_url: '', date_of_birth: '',
  });
  const [emergencyForm, setEmergencyForm] = useState<MeEmergencyContact>({
    name: '', phone: '', relationship: '',
  });

  const fetchProfile = useCallback(async () => {
    setLoading(true);
    try {
      setProfile(await meApi.getProfile());
    } catch {
      setProfile(null);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchDocs = useCallback(async () => {
    setDocsLoading(true);
    try {
      setDocs(await meApi.documents.list());
    } catch {
      setDocs([]);
    } finally {
      setDocsLoading(false);
    }
  }, []);

  // Lazy-load preferences on first visit to the Notifications tab.
  // Wrapped in try/catch so a 4xx doesn't black out the rest of
  // the page; the existing default-on state stays.
  const fetchPrefs = useCallback(async () => {
    try {
      const r = await meApi.notificationPreferences.get();
      const channels = (r as { channels?: Record<string, boolean> } | null)?.channels;
      if (channels && typeof channels === 'object') {
        setPrefs((p) => ({ ...p, ...channels }));
      }
    } catch {
      // keep defaults
    } finally {
      setPrefsLoaded(true);
    }
  }, []);

  // P0.3 — worker's own digest subscription list. Same lazy-load
  // lifecycle as prefs; both are gated by tab='notifications'.
  const fetchDigests = useCallback(async () => {
    try {
      setDigests(await digestsApi.list());
    } catch {
      // keep empty
    } finally {
      setDigestsLoaded(true);
    }
  }, []);

  const handleAddPersonalDigest = async (cadence: DigestCadence) => {
    if (digestsBusy) return;
    setDigestsBusy(true);
    try {
      await digestsApi.create({
        report_type: 'attendance_worker_personal',
        cadence,
      });
      setToast(t('worker:profile.toastSubscribed', { cadence }));
      await fetchDigests();
    } catch (e) {
      setToast(e instanceof ApiError ? translateApiError(e, t) : t('common:feedback.actionFailed'));
    } finally {
      setDigestsBusy(false);
    }
  };

  const handleToggleDigest = async (sub: DigestSubscriptionResponse) => {
    if (digestsBusy) return;
    setDigestsBusy(true);
    try {
      await digestsApi.update(sub.id, { enabled: !sub.enabled });
      await fetchDigests();
    } catch (e) {
      setToast(e instanceof ApiError ? translateApiError(e, t) : t('common:feedback.actionFailed'));
    } finally {
      setDigestsBusy(false);
    }
  };

  const handleChangeDigestCadence = async (
    sub: DigestSubscriptionResponse, cadence: DigestCadence,
  ) => {
    if (digestsBusy) return;
    setDigestsBusy(true);
    try {
      await digestsApi.update(sub.id, { cadence });
      await fetchDigests();
    } catch (e) {
      setToast(e instanceof ApiError ? translateApiError(e, t) : t('common:feedback.actionFailed'));
    } finally {
      setDigestsBusy(false);
    }
  };

  const handleDeleteDigest = async (sub: DigestSubscriptionResponse) => {
    if (digestsBusy) return;
    setDigestsBusy(true);
    try {
      await digestsApi.delete(sub.id);
      setToast(t('worker:profile.toastSubscriptionRemoved'));
      await fetchDigests();
    } catch (e) {
      setToast(e instanceof ApiError ? translateApiError(e, t) : t('common:feedback.actionFailed'));
    } finally {
      setDigestsBusy(false);
    }
  };

  // Optimistic toggle handler: flip local first, write through,
  // revert + show error on failure. Mirrors the mobile Profile
  // screen handler from svdy-mobile/app/(tabs)/profile.tsx.
  const handleToggleChannel = async (
    key: 'in_app' | 'email' | 'push' | 'clock_reminder',
    next: boolean,
  ) => {
    if (prefsBusy) return;
    setPrefsError(null);
    const prev = { ...prefs };
    const updated = { ...prefs, [key]: next };
    setPrefs(updated);
    setPrefsBusy(true);
    try {
      await meApi.notificationPreferences.save(updated);
      const chName = t(`worker:profile.ch${
        key === 'in_app' ? 'InApp'
        : key === 'email' ? 'Email'
        : key === 'push' ? 'Push'
        : 'ClockReminder'
      }`);
      setToast(next
        ? t('worker:profile.toastChannelEnabled', { channel: chName })
        : t('worker:profile.toastChannelDisabled', { channel: chName }));
    } catch (e) {
      setPrefs(prev);
      setPrefsError(
        e instanceof ApiError ? translateApiError(e, t) : t('common:feedback.actionFailed'),
      );
    } finally {
      setPrefsBusy(false);
    }
  };

  useEffect(() => { fetchProfile(); }, [fetchProfile]);
  useEffect(() => {
    if (tab === 'documents' && docs.length === 0) fetchDocs();
  }, [tab, docs.length, fetchDocs]);
  useEffect(() => {
    if (tab === 'notifications' && !prefsLoaded) fetchPrefs();
  }, [tab, prefsLoaded, fetchPrefs]);
  useEffect(() => {
    if (tab === 'notifications' && !digestsLoaded) fetchDigests();
  }, [tab, digestsLoaded, fetchDigests]);


  useEffect(() => {
    if (tab === 'devices' && !devicesLoaded) {
      void fetchDevices();
    }
    // intentionally only re-fetch on tab transition / explicit
    // refresh — no auto-poll since the list is stable enough
    // outside an admin-side force-revoke event the worker can't
    // observe in real-time anyway.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, devicesLoaded]);

  const fetchDevices = useCallback(async () => {
    setDevicesLoading(true);
    setDevicesError(null);
    try {
      // Don't include revoked rows — worker view focuses on live
      // devices they can act on. The admin Devices page is the
      // place that surfaces revoked rows for forensics.
      const rows = await meApi.devices.list(false);
      setDevices(rows);
    } catch (e) {
      setDevicesError(
        e instanceof ApiError ? translateApiError(e, t) : t('common:feedback.loadFailed'),
      );
    } finally {
      setDevicesLoading(false);
      setDevicesLoaded(true);
    }
  }, [t]);

  const handleConfirmRevokeDevice = async () => {
    if (!revokeDevTarget) return;
    setRevokingDev(true);
    try {
      await meApi.devices.revoke(revokeDevTarget.id);
      setToast(t('worker:profile.toastDeviceRevoked', { platform: revokeDevTarget.platform }));
      setRevokeDevTarget(null);
      // Drop the row locally rather than re-fetch — backend
      // keeps the row with revoked_at populated; we're filtering
      // include_revoked=false so the next refresh would drop it
      // anyway. Avoids an extra round-trip on the typical path.
      setDevices((prev) => prev.filter((d) => d.id !== revokeDevTarget.id));
    } catch (e) {
      setToast(e instanceof ApiError ? translateApiError(e, t) : t('common:feedback.actionFailed'));
    } finally {
      setRevokingDev(false);
    }
  };

  const handleExportData = async () => {
    setExportLoading(true);
    try {
      const data = await meApi.gdpr.exportData();
      // Trigger a browser download. Blob → object URL → anchor click.
      // Inline JSON for now (Phase 1); a future iteration switches to
      // a backend-signed S3 link for multi-MB payloads.
      // GDPR personal-data export: JSON, not a table, so it is the delivery helper
      // rather than `exportTable`. Same Safari revoke-timing fix as the other two.
      downloadText(
        JSON.stringify(data, null, 2),
        `svdy-data-export-${new Date().toISOString().slice(0, 10)}.json`,
        'application/json',
      );
      setToast(t('worker:profile.toastExportDownloaded'));
    } catch (e) {
      setToast(e instanceof ApiError ? translateApiError(e, t) : t('common:feedback.actionFailed'));
    } finally {
      setExportLoading(false);
    }
  };

  const handleConfirmDeletion = async () => {
    setDeletionLoading(true);
    try {
      const r = await meApi.gdpr.requestAccountDeletion();
      setToast(t('worker:profile.toastDeletionRequested'));
      setDeletionConfirmOpen(false);
      // Refresh profile so the Privacy tab flips to the
      // already-requested state.
      const fresh = await meApi.getProfile();
      setProfile(fresh);
      // Sanity log so the dev console shows the timestamp the
      // backend wrote.
      // eslint-disable-next-line no-console
      console.info('[gdpr] deletion requested at', r.deletion_requested_at);
    } catch (e) {
      setToast(e instanceof ApiError ? translateApiError(e, t) : t('worker:profile.deletionRequestFailed'));
    } finally {
      setDeletionLoading(false);
    }
  };

  // Privacy audit Wave 2 — biometric consent toggle. Optimistic update
  // pattern matches notification preferences; revert on failure.
  //
  // Issue 4 (Path C 2026-05-22) — Grant / revoke MOVED to the
  // dedicated /me/face-template page (single source of truth per
  // svdy-rules §10.5). The Privacy tab now renders a status-only
  // banner + "Manage face matching →" deep-link; the inline
  // grant/revoke buttons + their state + their confirm dialog were
  // removed. Two surfaces with the same write actions = guaranteed
  // drift on the next text/UX change.

  const openPersonal = () => {
    setPersonalForm({
      phone: profile?.phone ?? '',
      avatar_url: profile?.avatar_url ?? '',
      date_of_birth: profile?.date_of_birth ?? '',
    });
    setPersonalOpen(true);
  };
  const openEmergency = () => {
    setEmergencyForm({
      name: profile?.emergency_contact.name ?? '',
      phone: profile?.emergency_contact.phone ?? '',
      relationship: profile?.emergency_contact.relationship ?? '',
    });
    setEmergencyOpen(true);
  };

  const savePartial = async (payload: MeProfileUpdate, label: string) => {
    setSaving(true);
    try {
      const updated = await meApi.updateProfile(payload);
      setProfile(updated);
      setToast(t('worker:profile.toastSaved', { label }));
      return true;
    } catch (e) {
      setToast(e instanceof ApiError ? translateApiError(e, t) : t('worker:profile.toastSaveFailed', { label }));
      return false;
    } finally {
      setSaving(false);
    }
  };

  const savePersonal = async () => {
    const payload: MeProfileUpdate = {};
    if (personalForm.phone !== (profile?.phone ?? '')) {
      payload.phone = personalForm.phone || undefined;
    }
    if (personalForm.avatar_url !== (profile?.avatar_url ?? '')) {
      payload.avatar_url = personalForm.avatar_url || undefined;
    }
    if (personalForm.date_of_birth !== (profile?.date_of_birth ?? '')) {
      payload.date_of_birth = personalForm.date_of_birth || undefined;
    }
    if (Object.keys(payload).length === 0) {
      setPersonalOpen(false);
      return;
    }
    if (await savePartial(payload, t('worker:profile.tabPersonal'))) setPersonalOpen(false);
  };

  const saveEmergency = async () => {
    const ok = await savePartial({ emergency_contact: emergencyForm }, t('worker:profile.emergencyTitle'));
    if (ok) setEmergencyOpen(false);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress size={28} />
      </Box>
    );
  }
  if (!profile) {
    return (
      <Box sx={{ p: 4 }}>
        <Typography variant="body2" color="error">{t('worker:profile.failedToLoad')}</Typography>
      </Box>
    );
  }

  const displayName = `${profile.first_name} ${profile.last_name}`.trim();
  const initials = displayName
    .split(' ')
    .map((p) => p[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();

  return (
    <>
    <Box sx={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'flex-start',
      py: '12px',
      width: '100%',
    }}>
      {/* Header */}
      <Box sx={{ width: '100%', mb: 1.5 }}>
        <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1.125rem' }}>
          {t('worker:profile.headerTitle')}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {t('worker:profile.headerSubtitle')}
        </Typography>
      </Box>

      {/* Identity card (always visible above the tabs) */}
      <Paper
        elevation={0}
        sx={{
          width: '100%',
          maxWidth: 720,
          borderRadius: 2,
          border: '1px solid',
          borderColor: 'divider',
          mb: 2,
          p: 2.5,
          display: 'flex',
          alignItems: 'center',
          gap: 2,
        }}
      >
        <Avatar
          src={profile.avatar_url ?? undefined}
          sx={{ width: 56, height: 56, bgcolor: 'primary.main', fontSize: '1.25rem' }}
        >
          {initials}
        </Avatar>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography variant="body1" sx={{ fontWeight: 600 }}>
            {displayName}
          </Typography>
          <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap>
            {profile.email && (
              <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.4, color: 'text.secondary' }}>
                <EmailOutlined sx={{ fontSize: 14 }} />
                <Typography variant="caption">{profile.email}</Typography>
              </Box>
            )}
            {profile.phone && (
              <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.4, color: 'text.secondary' }}>
                <PhoneOutlined sx={{ fontSize: 14 }} />
                <Typography variant="caption">{profile.phone}</Typography>
              </Box>
            )}
            <Chip
              label={profile.status}
              size="small"
              color={profile.status === 'active' ? 'success' : 'default'}
              variant="outlined"
              sx={{ height: 18, fontSize: '0.65rem' }}
            />
          </Stack>
        </Box>
      </Paper>

      {/* Tab strip */}
      <Box sx={{ width: '100%', maxWidth: 720, borderBottom: 1, borderColor: 'divider', mb: 2 }}>
        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v as TabKey)}
          sx={{
            minHeight: 36,
            '& .MuiTab-root': { textTransform: 'none', minHeight: 36, fontSize: '0.8125rem' },
          }}
        >
          <Tab value="personal" label={t('worker:profile.tabPersonal')} />
          <Tab value="employment" label={t('worker:profile.tabEmployment')} />
          <Tab value="emergency" label={t('worker:profile.tabEmergency')} />
          <Tab value="documents" label={t('worker:profile.tabDocuments')} />
          <Tab value="notifications" label={t('worker:profile.tabNotifications')} />
          <Tab value="devices" label={t('worker:profile.tabDevices')} />
          <Tab value="privacy" label={t('worker:profile.tabPrivacy')} />
        </Tabs>
      </Box>

      {/* Tab panels */}
      <Box sx={{ width: '100%', maxWidth: 720 }}>
        {tab === 'personal' && (
          <Paper elevation={0} sx={{ p: 2.5, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, fontSize: '0.9rem' }}>
                Personal
              </Typography>
              <Button
                size="small"
                variant="outlined"
                startIcon={<EditOutlined sx={{ fontSize: 14 }} />}
                onClick={openPersonal}
                sx={{ borderRadius: 1, textTransform: 'none', fontSize: '0.75rem', py: 0 }}
              >
                Edit
              </Button>
            </Stack>
            <Divider sx={{ mb: 1.5 }} />
            <Stack spacing={0.5}>
              <Row label={t('worker:profile.labelEmail')} value={profile.email} />
              <Row label={t('worker:profile.labelPhone')} value={profile.phone} />
              <Row label={t('worker:profile.labelDateOfBirth')} value={profile.date_of_birth} />
              <Row label={t('worker:profile.labelAvatarUrl')} value={profile.avatar_url} />
            </Stack>
          </Paper>
        )}

        {tab === 'employment' && (
          <Paper elevation={0} sx={{ p: 2.5, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, fontSize: '0.9rem' }}>
                {t('worker:profile.tabEmployment')}
              </Typography>
              <Tooltip title={t('worker:profile.readOnlyTooltip')}>
                <Chip
                  label={t('worker:profile.readOnly')}
                  size="small"
                  color="default"
                  variant="outlined"
                  sx={{ height: 18, fontSize: '0.65rem' }}
                />
              </Tooltip>
            </Stack>
            <Divider sx={{ mb: 1.5 }} />
            <Stack spacing={0.5}>
              <Row label={t('worker:profile.labelWorkerCode')} value={profile.worker_code} />
              <Row label={t('worker:profile.labelPosition')} value={profile.position} />
              <Row label={t('worker:profile.labelDepartment')} value={profile.department} />
              <Row label={t('worker:profile.labelEmploymentType')} value={profile.employment_type} />
              <Row label={t('worker:profile.labelHireDate')} value={profile.hire_date} />
            </Stack>
          </Paper>
        )}

        {tab === 'emergency' && (
          <Paper elevation={0} sx={{ p: 2.5, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
            <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 1 }}>
              <Box sx={{ minWidth: 0, pr: 2 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, fontSize: '0.9rem' }}>
                  {t('worker:profile.emergencyTitle')}
                  <Typography
                    component="span"
                    variant="caption"
                    color="text.disabled"
                    sx={{ fontWeight: 400, ml: 1, fontSize: '0.7rem' }}
                  >
                    {t('worker:profile.optionalLabel')}
                  </Typography>
                </Typography>
                {/* Purpose disclosure (GDPR Art. 5(1)(a) lawfulness, fairness, transparency).
                    Worker explicitly sees who reads + when this is used before deciding to
                    fill it in. */}
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: 'block', mt: 0.5, fontSize: '0.75rem', lineHeight: 1.4 }}
                >
                  {t('worker:profile.emergencyDisclosure')}
                </Typography>
              </Box>
              <Button
                size="small"
                variant="outlined"
                startIcon={<EditOutlined sx={{ fontSize: 14 }} />}
                onClick={openEmergency}
                sx={{ borderRadius: 1, textTransform: 'none', fontSize: '0.75rem', py: 0, flexShrink: 0 }}
              >
                {profile.emergency_contact.name || profile.emergency_contact.phone ? t('worker:profile.editBtn') : t('worker:profile.addContact')}
              </Button>
            </Stack>
            <Divider sx={{ my: 1.5 }} />
            {profile.emergency_contact.name || profile.emergency_contact.phone || profile.emergency_contact.relationship ? (
              <Stack spacing={0.5}>
                <Row label={t('worker:profile.labelName')} value={profile.emergency_contact.name} />
                <Row label={t('worker:profile.labelPhone')} value={profile.emergency_contact.phone} />
                <Row label={t('worker:profile.labelRelationship')} value={
                  profile.emergency_contact.relationship
                    ? ((RELATIONSHIP_CODES as readonly string[]).includes(profile.emergency_contact.relationship)
                        ? t(`worker:profile.rel.${profile.emergency_contact.relationship}`)
                        : profile.emergency_contact.relationship)
                    : null
                } />
              </Stack>
            ) : (
              <Typography variant="caption" color="text.disabled" sx={{ display: 'block', py: 1 }}
                dangerouslySetInnerHTML={{ __html: t('worker:profile.emergencyEmpty') }}
              />
            )}
          </Paper>
        )}

        {tab === 'documents' && (
          <Paper elevation={0} sx={{ p: 2.5, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, fontSize: '0.9rem', mb: 1.5 }}>
              {t('worker:profile.documentsTitle')}
            </Typography>
            <Divider sx={{ mb: 1.5 }} />
            {docsLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress size={20} />
              </Box>
            ) : docs.length === 0 ? (
              <Typography variant="caption" color="text.disabled">
                {t('worker:profile.noDocuments')}
              </Typography>
            ) : (
              <Stack spacing={1}>
                {docs.map((d) => (
                  <Box
                    key={d.id}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      p: 1,
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 1.5,
                    }}
                  >
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
                      <InsertDriveFileOutlined sx={{ color: 'text.secondary', fontSize: 18 }} />
                      <Box sx={{ minWidth: 0 }}>
                        <Typography variant="body2" sx={{ fontWeight: 500 }} noWrap>
                          {d.file_name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {fmtBytes(d.file_size)}
                          {d.uploaded_at ? ` · ${t('worker:profile.docUploaded', { date: new Date(d.uploaded_at).toLocaleDateString(activeLocale()) })}` : ''}
                        </Typography>
                      </Box>
                    </Stack>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => openDocument(d.file_url, d.file_name)}
                      startIcon={<OpenInNewOutlined sx={{ fontSize: 14 }} />}
                      sx={{ borderRadius: 1, textTransform: 'none', fontSize: '0.75rem', py: 0 }}
                    >
                      {t('worker:profile.openBtn')}
                    </Button>
                  </Box>
                ))}
              </Stack>
            )}
          </Paper>
        )}

        {tab === 'notifications' && (
          <Paper elevation={0} sx={{ p: 2.5, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, fontSize: '0.9rem', mb: 0.5 }}>
              {t('worker:profile.notifChannelsTitle')}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
              {t('worker:profile.notifChannelsDesc')}
            </Typography>
            <Divider sx={{ mb: 1.5 }} />
            {prefsError ? (
              <Alert severity="error" variant="outlined" sx={{ mb: 1.5, fontSize: '0.8rem' }}
                onClose={() => setPrefsError(null)}>
                {prefsError}
              </Alert>
            ) : null}
            {(['in_app', 'email', 'push', 'clock_reminder'] as const).map((key) => (
              <Box
                key={key}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  py: 1,
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                  '&:last-child': { borderBottom: 0 },
                }}
              >
                <Box sx={{ minWidth: 0, pr: 2 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {t(`worker:profile.ch${
                      key === 'in_app' ? 'InApp'
                      : key === 'email' ? 'Email'
                      : key === 'push' ? 'Push'
                      : 'ClockReminder'
                    }`)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {key === 'in_app' && t('worker:profile.chInAppDesc')}
                    {key === 'email' && t('worker:profile.chEmailDesc')}
                    {key === 'push' && t('worker:profile.chPushDesc')}
                    {key === 'clock_reminder' && t('worker:profile.chClockReminderDesc')}
                  </Typography>
                </Box>
                <Switch
                  checked={key === 'clock_reminder' ? prefs[key] !== false : Boolean(prefs[key])}
                  disabled={prefsBusy}
                  onChange={(_, v) => void handleToggleChannel(key, v)}
                  size="small"
                />
              </Box>
            ))}
            <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mt: 1.5 }}>
              {t('worker:profile.notifChannelsFootnote')}
            </Typography>
          </Paper>
        )}

        {tab === 'notifications' && (
          <>
          <Paper elevation={0} sx={{ p: 2.5, borderRadius: 2, border: '1px solid', borderColor: 'divider', mt: 2 }}>
            {(() => {
              const isPro = tierAtLeast(profile?.plan_tier, 'professional');
              const personalSub = digests.find((d) => d.report_type === 'attendance_worker_personal');
              return (
                <>
                  <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, fontSize: '0.9rem' }}>
                      {t('worker:profile.notifEmailDigests')}
                    </Typography>
                    {!isPro && (
                      <Chip
                        label={t('worker:profile.professional')}
                        size="small"
                        sx={{ height: 18, fontSize: '0.65rem', borderRadius: 0.75 }}
                      />
                    )}
                  </Stack>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
                    {t('worker:profile.notificationsBody')}
                    {!isPro && <> {t('worker:profile.availableOnThe')} <strong>{t('worker:profile.proPlan')}</strong>.</>}
                  </Typography>
                  <Divider sx={{ mb: 1.5 }} />

                  {!personalSub ? (
                    <Stack direction="row" spacing={1} flexWrap="wrap">
                      <Button
                        size="small"
                        variant="outlined"
                        disabled={!isPro || digestsBusy}
                        onClick={() => void handleAddPersonalDigest('daily')}
                        sx={{ borderRadius: 1, textTransform: 'none' }}
                      >
                        {t('worker:profile.notifSubscribeDaily')}
                      </Button>
                      <Button
                        size="small"
                        variant="outlined"
                        disabled={!isPro || digestsBusy}
                        onClick={() => void handleAddPersonalDigest('weekly')}
                        sx={{ borderRadius: 1, textTransform: 'none' }}
                      >
                        {t('worker:profile.notifSubscribeWeekly')}
                      </Button>
                    </Stack>
                  ) : (
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1.5,
                        py: 1,
                      }}
                    >
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography sx={{ fontWeight: 600, fontSize: '0.875rem' }}>
                          {t('worker:profile.notifMyTimeSummary')}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {personalSub.last_sent_at
                            ? t('worker:profile.notifLastSent', { when: new Date(personalSub.last_sent_at).toLocaleString(activeLocale()) })
                            : t('worker:profile.notifNotYetSent')}
                        </Typography>
                      </Box>
                      <Button
                        size="small"
                        variant={personalSub.cadence === 'daily' ? 'contained' : 'outlined'}
                        disabled={!isPro || digestsBusy}
                        onClick={() => void handleChangeDigestCadence(personalSub, 'daily')}
                        sx={{ borderRadius: 1, textTransform: 'none', minWidth: 64 }}
                      >
                        {t('worker:profile.notifDaily')}
                      </Button>
                      <Button
                        size="small"
                        variant={personalSub.cadence === 'weekly' ? 'contained' : 'outlined'}
                        disabled={!isPro || digestsBusy}
                        onClick={() => void handleChangeDigestCadence(personalSub, 'weekly')}
                        sx={{ borderRadius: 1, textTransform: 'none', minWidth: 64 }}
                      >
                        {t('worker:profile.notifWeekly')}
                      </Button>
                      <Switch
                        checked={personalSub.enabled}
                        disabled={!isPro || digestsBusy}
                        onChange={() => void handleToggleDigest(personalSub)}
                        size="small"
                      />
                      <Button
                        size="small"
                        variant="text"
                        color="error"
                        disabled={!isPro || digestsBusy}
                        onClick={() => void handleDeleteDigest(personalSub)}
                        sx={{ borderRadius: 1, textTransform: 'none', minWidth: 0, px: 1 }}
                      >
                        {t('worker:profile.notifRemove')}
                      </Button>
                    </Box>
                  )}
                </>
              );
            })()}
          </Paper>

          </>
        )}

        {tab === 'devices' && (
          <Paper elevation={0} sx={{ p: 2.5, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, fontSize: '0.9rem', mb: 0.5 }}>
              {t('worker:devices.title')}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
              {t('worker:devices.desc')}
            </Typography>
            <Divider sx={{ mb: 1.5 }} />
            {devicesError ? (
              <Alert severity="error" variant="outlined" sx={{ mb: 1.5, fontSize: '0.8rem' }}
                onClose={() => setDevicesError(null)}>
                {devicesError}
              </Alert>
            ) : null}
            {devicesLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress size={20} />
              </Box>
            ) : devices.length === 0 ? (
              <Typography variant="caption" color="text.disabled">
                {t('worker:devices.noDevices')}
              </Typography>
            ) : (
              <Stack spacing={1}>
                {devices.map((d) => (
                  <Box
                    key={d.id}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      p: 1.25,
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 1.5,
                      gap: 1.5,
                    }}
                  >
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Chip
                          label={d.platform === 'ios' ? 'iOS' : d.platform === 'android' ? 'Android' : d.platform}
                          size="small"
                          color={d.platform === 'ios' ? 'primary' : d.platform === 'android' ? 'success' : 'default'}
                          variant="outlined"
                          sx={{ height: 20, fontSize: '0.7rem' }}
                        />
                        <Typography variant="body2" sx={{ fontWeight: 500 }} noWrap>
                          {d.device_id ?? '(no model)'}
                        </Typography>
                      </Stack>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
                        {t('worker:devices.lastActive', { when: d.last_active_at ? new Date(d.last_active_at).toLocaleString(activeLocale()) : '—' })}
                        {d.app_version ? ` · v${d.app_version}` : ''}
                      </Typography>
                    </Box>
                    <Button
                      size="small"
                      variant="outlined"
                      color="error"
                      startIcon={<BlockOutlined sx={{ fontSize: 14 }} />}
                      onClick={() => setRevokeDevTarget(d)}
                      sx={{ borderRadius: 1, textTransform: 'none', fontSize: '0.75rem', py: 0 }}
                    >
                      {t('worker:devices.revoke')}
                    </Button>
                  </Box>
                ))}
              </Stack>
            )}
            <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mt: 1.5 }}>
              {t('worker:devices.lostPhone')}
            </Typography>
          </Paper>
        )}

        {/* Privacy / GDPR — self-service data export + account
            deletion (S7 §2.2). Worker-controlled; admin reverse via
            operator console while the 30-day grace is open. */}
        {tab === 'privacy' && (
          <Stack spacing={2}>
            {/* Transparency disclosure (GDPR Art. 5(1)(a) — lawfulness, fairness, transparency).
                Workers must know exactly what data we keep + why before
                they can make informed choices about Download / Request
                deletion below. Privacy audit 2026-05-22 added this section.*/}
            <Paper
              elevation={0}
              sx={{
                p: 2.5, borderRadius: 2,
                border: '1px solid', borderColor: 'divider',
                background: (t) => t.palette.mode === 'dark' ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.015)',
              }}
            >
              <Typography variant="subtitle2" sx={{ fontWeight: 700, fontSize: '0.9rem', mb: 0.5 }}>
                {t('worker:privacy.whatWeCollect')}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2, fontSize: '0.8125rem' }}>
                {t('worker:privacy.transparency')}
              </Typography>
              <Stack divider={<Divider />} spacing={0}>
                {(t('worker:privacy.dataRows', { returnObjects: true }) as Array<{ field: string; audience: string; purpose: string }>).map((row) => (
                  <Box
                    key={row.field}
                    sx={{ py: 1.25, display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '180px 140px 1fr' }, gap: 1, alignItems: 'flex-start' }}
                  >
                    <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.8125rem' }}>
                      {row.field}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                      {row.audience}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem', lineHeight: 1.45 }}>
                      {row.purpose}
                    </Typography>
                  </Box>
                ))}
              </Stack>
              <Alert severity="info" variant="outlined" sx={{ mt: 2, fontSize: '0.75rem' }}>
                {t('worker:privacy.note')}
              </Alert>
            </Paper>

            {/* Privacy audit Wave 2 — biometric consent.
                GDPR Art. 9(2)(a) requires explicit consent for biometric
                data processing.
                Issue 4 (Path C 2026-05-22) — this card was simplified
                to a STATUS BANNER + DEEP LINK to the dedicated
                /me/face-template page. The inline Grant / Revoke
                buttons moved there to keep one source of truth for
                the actual mutations (svdy-rules §10.5). Apple's
                Settings → Privacy → Face ID and Stripe's Identity
                Verification panel use the same pattern: status here,
                manage in the dedicated panel.

                QuTime-only, fail-closed (2026-08-14). A PureOA-only org
                captures no biometrics, so this card was offering its workers a
                consent status for data that is never collected, on a card whose
                only action deep-links to a route that now denies. The gate is
                `role?.hasQutime` — `role === null` (first paint or a failed
                licence fetch) renders nothing rather than guessing. */}
            {role?.hasQutime && (
            <Paper
              elevation={0}
              role="link"
              tabIndex={0}
              onClick={() => navigate('/me/face-template')}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  navigate('/me/face-template');
                }
              }}
              sx={{
                p: 2.5,
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'divider',
                cursor: 'pointer',
                transition: 'border-color 120ms',
                '&:hover': { borderColor: 'primary.main' },
              }}
            >
              <Stack direction="row" spacing={1.5} alignItems="center">
                <FaceOutlined
                  sx={{
                    fontSize: 28,
                    color: profile?.face_template_consented_at
                      ? 'success.main'
                      : 'warning.main',
                  }}
                />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Stack
                    direction="row"
                    alignItems="center"
                    spacing={1}
                    sx={{ mb: 0.25 }}
                  >
                    <Typography
                      variant="subtitle2"
                      sx={{ fontWeight: 700, fontSize: '0.9rem' }}
                    >
                      {t('worker:privacy.faceMatching')}
                    </Typography>
                    <Chip
                      size="small"
                      variant="outlined"
                      color={profile?.face_template_consented_at ? 'success' : 'default'}
                      label={
                        profile?.face_template_consented_at
                          ? t('worker:privacy.enabled')
                          : t('worker:privacy.notSetUp')
                      }
                      sx={{ height: 20, fontSize: '0.65rem' }}
                    />
                  </Stack>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ fontSize: '0.8125rem', lineHeight: 1.5 }}
                  >
                    {profile?.face_template_consented_at
                      ? t('worker:privacy.consentOnFile', { date: profile.face_template_consented_at ? new Date(profile.face_template_consented_at).toLocaleDateString(activeLocale()) : 'recently' })
                      : t('worker:privacy.biometricDisabled')}
                  </Typography>
                </Box>
                <ArrowForwardOutlined
                  sx={{ color: 'text.secondary', fontSize: 18, flexShrink: 0 }}
                />
              </Stack>
            </Paper>
            )}

            <Paper elevation={0} sx={{ p: 2.5, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, fontSize: '0.9rem', mb: 0.5 }}>
                {t('worker:privacy.downloadTitle')}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {t('worker:privacy.downloadDesc')}
              </Typography>
              <Button
                variant="outlined"
                size="small"
                disabled={exportLoading}
                onClick={handleExportData}
                sx={{ borderRadius: 1, textTransform: 'none' }}
              >
                {exportLoading ? <CircularProgress size={16} /> : t('worker:privacy.downloadBtn')}
              </Button>
            </Paper>
            <Paper elevation={0} sx={{ p: 2.5, borderRadius: 2, border: '1px solid', borderColor: 'error.light' }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, fontSize: '0.9rem', mb: 0.5, color: 'error.main' }}>
                {t('worker:privacy.deleteTitle')}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                {t('worker:privacy.deleteDesc')}
              </Typography>
              {profile?.deletion_requested_at ? (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  {t('worker:privacy.deleteAlready', { date: new Date(profile.deletion_requested_at).toLocaleDateString(activeLocale()) })}
                </Alert>
              ) : null}
              <Button
                variant="outlined"
                color="error"
                size="small"
                disabled={deletionLoading || !!profile?.deletion_requested_at}
                onClick={() => setDeletionConfirmOpen(true)}
                sx={{ borderRadius: 1, textTransform: 'none' }}
              >
                {deletionLoading ? <CircularProgress size={16} /> : t('worker:privacy.deleteBtn')}
              </Button>
            </Paper>
          </Stack>
        )}
      </Box>

      {/* Account deletion confirm dialog */}
      <Dialog
        open={deletionConfirmOpen}
        onClose={() => !deletionLoading && setDeletionConfirmOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        <DialogTitle sx={{ py: 2 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, fontSize: '0.9rem', color: 'error.main' }}>
            {t('worker:privacy.confirmTitle')}
          </Typography>
        </DialogTitle>
        <DialogContent dividers sx={{ py: 3 }}>
          <DialogContentText sx={{ fontSize: '0.875rem' }}>
            {t('worker:privacy.confirmDesc')}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ py: 2 }}>
          <Button
            onClick={() => setDeletionConfirmOpen(false)}
            disabled={deletionLoading}
            sx={{ borderRadius: 1, textTransform: 'none' }}
          >
            {t('worker:privacy.cancel')}
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleConfirmDeletion}
            disabled={deletionLoading}
            sx={{ borderRadius: 1, textTransform: 'none' }}
          >
            {deletionLoading ? <CircularProgress size={18} /> : t('worker:privacy.deleteBtn')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Worker self-revoke device confirm */}
      <Dialog
        open={!!revokeDevTarget}
        onClose={() => !revokingDev && setRevokeDevTarget(null)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        <DialogTitle sx={{ py: 2 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, fontSize: '0.9rem' }}>
            {t('worker:profile.revokeDeviceTitle')}
          </Typography>
        </DialogTitle>
        <DialogContent dividers sx={{ py: 3 }}>
          <DialogContentText sx={{ fontSize: '0.875rem' }}>
            {t('worker:profile.revokeDeviceDesc', { platform: revokeDevTarget?.platform })}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ py: 2 }}>
          <Button
            onClick={() => setRevokeDevTarget(null)}
            disabled={revokingDev}
            sx={{ borderRadius: 1, textTransform: 'none' }}
          >
            {t('worker:profile.dialogCancel')}
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleConfirmRevokeDevice}
            disabled={revokingDev}
            sx={{ borderRadius: 1, textTransform: 'none' }}
          >
            {revokingDev ? <CircularProgress size={18} /> : t('worker:devices.revoke')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Personal edit dialog */}
      <Dialog open={personalOpen} onClose={() => !saving && setPersonalOpen(false)} maxWidth="sm" fullWidth
        PaperProps={{ sx: { borderRadius: 2 } }}>
        <DialogTitle sx={{ py: 2 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, fontSize: '0.9rem' }}>
            {t('worker:profile.dialogEditPersonal')}
          </Typography>
        </DialogTitle>
        <DialogContent dividers sx={{ py: 3 }}>
          <Stack spacing={2}>
            <PhoneField
              label={t('worker:profile.labelPhone')}
              value={personalForm.phone}
              onChange={(v) => setPersonalForm((p) => ({ ...p, phone: v || '' }))}
              fullWidth
              size="small"
            />
            <TextField
              label={t('worker:profile.labelAvatarUrl')}
              size="small"
              fullWidth
              value={personalForm.avatar_url}
              onChange={(e) => setPersonalForm((p) => ({ ...p, avatar_url: e.target.value }))}
            />
            <DateField
              label={t('worker:profile.labelDateOfBirth')}
              size="small"
              fullWidth
              value={personalForm.date_of_birth}
              onChange={(e) => setPersonalForm((p) => ({ ...p, date_of_birth: e.target.value }))}
              helperText="Optional. Used for birthday recognition. Your manager and HR can see it. Leave blank to opt out."
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ py: 2 }}>
          <Button onClick={() => setPersonalOpen(false)} disabled={saving}
            sx={{ borderRadius: 1, textTransform: 'none' }}>{t('worker:profile.dialogCancel')}</Button>
          <Button onClick={savePersonal} variant="contained" disabled={saving}
            sx={{ borderRadius: 1, textTransform: 'none' }}>
            {saving ? <CircularProgress size={16} /> : t('worker:profile.dialogSave')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Emergency contact edit dialog */}
      <Dialog open={emergencyOpen} onClose={() => !saving && setEmergencyOpen(false)} maxWidth="sm" fullWidth
        PaperProps={{ sx: { borderRadius: 2 } }}>
        <DialogTitle sx={{ py: 2 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, fontSize: '0.9rem' }}>
            {t('worker:profile.dialogEditEmergency')}
          </Typography>
        </DialogTitle>
        <DialogContent dividers sx={{ py: 3 }}>
          <Stack spacing={2}>
            <Alert severity="info" variant="outlined" sx={{ fontSize: '0.8rem' }}>
              {t('worker:profile.dialogEmergencyInfo')}
            </Alert>
            <TextField
              label={t('worker:profile.labelName')}
              size="small"
              fullWidth
              value={emergencyForm.name ?? ''}
              onChange={(e) => setEmergencyForm((p) => ({ ...p, name: e.target.value }))}
            />
            <PhoneField
              label={t('worker:profile.labelPhone')}
              value={emergencyForm.phone ?? ''}
              onChange={(v) => setEmergencyForm((p) => ({ ...p, phone: v || '' }))}
              fullWidth
              size="small"
            />
            <TextField
              select
              label={t('worker:profile.labelRelationship')}
              size="small"
              fullWidth
              value={
                emergencyForm.relationship && (RELATIONSHIP_CODES as readonly string[]).includes(emergencyForm.relationship)
                  ? emergencyForm.relationship
                  : ''
              }
              onChange={(e) => setEmergencyForm((p) => ({ ...p, relationship: e.target.value }))}
            >
              <MenuItem value="">
                <em>{t('worker:profile.relSelect')}</em>
              </MenuItem>
              {RELATIONSHIP_CODES.map((code) => (
                <MenuItem key={code} value={code}>
                  {t(`worker:profile.rel.${code}`)}
                </MenuItem>
              ))}
            </TextField>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ py: 2 }}>
          <Button onClick={() => setEmergencyOpen(false)} disabled={saving}
            sx={{ borderRadius: 1, textTransform: 'none' }}>{t('worker:profile.dialogCancel')}</Button>
          <Button onClick={saveEmergency} variant="contained" disabled={saving}
            sx={{ borderRadius: 1, textTransform: 'none' }}>
            {saving ? <CircularProgress size={16} /> : t('worker:profile.dialogSave')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Home address dialog deleted — alembic 033 dropped home_address_*
          columns in the privacy audit (svdy/PRIVACY_AUDIT_2026-05-22.md). */}

      <Snackbar
        open={!!toast}
        autoHideDuration={4000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert severity="success" onClose={() => setToast(null)} sx={{ width: '100%' }}>
          {toast}
        </Alert>
      </Snackbar>
    </Box>
    {viewer}
    </>
  );
};

export default WorkerProfile;
